import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Loader2,
    Download,
    FilterX,
    PlusCircle,
    Edit,
    Trash2,
} from "lucide-react";
import constant from "../../constant";
import FileSaver from "file-saver";
import Papa from "papaparse";
import AddLedger from "./addledger";

const Ledger = ({ partyId, partyName, isVisible }) => {
    const navigate = useNavigate();
    const [ledgerData, setLedgerData] = useState(null);
    const [ledgerLoading, setLedgerLoading] = useState(false);
    const [ledgerError, setLedgerError] = useState(null);
    const [totalCredit, setTotalCredit] = useState(0);
    const [totalDebit, setTotalDebit] = useState(0);
    const [openingBalanceRow, setOpeningBalanceRow] = useState(null);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isFilteringLedger, setIsFilteringLedger] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const constructApiUrl = (endpoint) => {
        const userRole = localStorage.getItem("role");
        const adminSelectedUser = localStorage.getItem("adminSelectedUser");
        let apiUrl = `${constant.REACT_APP_API_URL}${endpoint}`;
        if (userRole === "admin" && adminSelectedUser) {
            apiUrl = `${constant.REACT_APP_API_URL}${endpoint}${endpoint.includes("?") ? "&" : "?"
                }userId=${adminSelectedUser}`;
        }
        return apiUrl;
    };

    const fetchLedgerData = useCallback(
        async (filterStartDate, filterEndDate) => {
            if (!partyId) return;

            setLedgerLoading(true);
            setIsFilteringLedger(!!(filterStartDate && filterEndDate));
            setLedgerError(null);
            setLedgerData(null);
            setOpeningBalanceRow(null);
            setTotalCredit(0);
            setTotalDebit(0);

            try {
                const token = localStorage.getItem("token");
                if (!token) throw new Error("Authentication token not found.");

                let apiUrl = constructApiUrl(`/ledger?partyId=${partyId}`);

                if (filterStartDate && filterEndDate) {
                    const [s_year, s_month, s_day] = filterStartDate.split("-");
                    const formattedStartDate = `${s_day}-${s_month}-${s_year}`;
                    const [e_year, e_month, e_day] = filterEndDate.split("-");
                    const formattedEndDate = `${e_day}-${e_month}-${e_year}`;
                    apiUrl += `&startDate=${formattedStartDate}&endDate=${formattedEndDate}`;
                }

                const response = await fetch(apiUrl, { headers: { Authorization: `Bearer ${token}` } });
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to fetch ledger data");
                }
                const result = await response.json();
                let fetchedEntries = result.data.ledgerData || [];



                if (!fetchedEntries || fetchedEntries.length === 0) {
                    setLedgerData([]);
                    setOpeningBalanceRow({ date: null, balance: '0.00', credit: 0, debit: 0 });
                    setTotalCredit(0);
                    setTotalDebit(0);
                    setLedgerLoading(false);
                    setIsFilteringLedger(false);
                    return;
                }

                fetchedEntries.sort((a, b) => new Date(a.transactionDate) - new Date(b.transactionDate));

                let initialBalance = 0;
                let openingEntryDetails = null;
                const firstOpeningEntry = fetchedEntries[0].voucherType === 'opening';
                let currentBalance = initialBalance;
                let runningCreditTotal = 0;
                let runningDebitTotal = 0;

                if (firstOpeningEntry) {
                    const openingDebit = parseFloat(fetchedEntries[0].debit || 0);
                    const openingCredit = parseFloat(fetchedEntries[0].credit || 0);
                    initialBalance = openingDebit - openingCredit;
                    currentBalance = openingDebit - openingCredit; //this is the line added
                    runningCreditTotal = fetchedEntries[0].credit || 0;
                    runningDebitTotal = fetchedEntries[0].debit || 0;

                    openingEntryDetails = {
                        date: firstOpeningEntry.transactionDate,
                        balance: initialBalance.toFixed(2),
                        credit: openingCredit,
                        debit: openingDebit,
                        id: fetchedEntries[0]._id,
                    };
                } else {
                    initialBalance = result.data.openingBalance || 0;
                    openingEntryDetails = {
                        date: null,
                        balance: initialBalance.toFixed(2),
                        credit: 0,
                        debit: 0,
                        id: null,
                    };
                }

                const transactionEntries = fetchedEntries
                    .filter(entry => entry.voucherType !== 'opening')
                    .sort((a, b) => new Date(a.transactionDate) - new Date(b.transactionDate));

                const processedEntries = transactionEntries.map((entry) => {
                    const credit = parseFloat(entry.credit || 0);
                    const debit = parseFloat(entry.debit || 0);
                    currentBalance = currentBalance - credit + debit;
                    runningCreditTotal += credit;
                    runningDebitTotal += debit;
                    return { ...entry, credit, debit, balance: currentBalance.toFixed(2) };
                });

                setOpeningBalanceRow(openingEntryDetails);
                setLedgerData(processedEntries);
                setTotalCredit(runningCreditTotal);
                setTotalDebit(runningDebitTotal);


            } catch (error) {
                console.error("Error fetching ledger:", error);
                setLedgerError(error.message || "An error occurred");
                setLedgerData([]);
                setOpeningBalanceRow({ date: null, balance: '0.00', credit: 0, debit: 0 });
                setTotalCredit(0);
                setTotalDebit(0);
            } finally {
                setLedgerLoading(false);
                setIsFilteringLedger(false);
            }
        },
        [partyId]
    );

    useEffect(() => {
        if (isVisible && partyId) {
            fetchLedgerData(startDate || null, endDate || null);
        } else {
            setLedgerData(null);
            setOpeningBalanceRow(null);
            setLedgerError(null);
            setTotalCredit(0);
            setTotalDebit(0);
            setStartDate("");
            setEndDate("");
        }

    }, [isVisible, partyId, fetchLedgerData]);

    const handleDateChange = (event) => {
        const { name, value } = event.target;
        let newStartDate = startDate;
        let newEndDate = endDate;

        if (name === "startDate") {
            newStartDate = value;
            setStartDate(value);
            if (newEndDate && value > newEndDate) {
                newEndDate = "";
                setEndDate("");
            }
        } else if (name === "endDate") {
            newEndDate = value;
            setEndDate(value);
        }

        fetchLedgerData(newStartDate || null, newEndDate || null);

    };

    const clearDateFilters = () => {
        setStartDate("");
        setEndDate("");
        fetchLedgerData(null, null);
    };


    const handleAddEntryClick = () => {
        setEditingEntry(null);
        setIsModalOpen(true);
    };

    const handleEditEntryClick = (entry) => {
        const entryToEdit = {
            ...entry,
            transactionDate: entry.transactionDate ? new Date(entry.transactionDate).toISOString().split('T')[0] : null,
        };
        setEditingEntry(entryToEdit);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingEntry(null);
        setIsSubmitting(false);
    };

    const handleFormSubmit = async (formData, entryId) => {
        setIsSubmitting(true);
        const token = localStorage.getItem("token");
        if (!token) {
            setIsSubmitting(false);
            navigate("/login");
            return;
        }

        const submissionData = { ...formData, partyId };

        const isEditing = !!entryId;
        const url = isEditing
            ? `${constant.REACT_APP_API_URL}/ledger/${entryId}`
            : `${constant.REACT_APP_API_URL}/ledger`;
        const method = isEditing ? "PATCH" : "POST";

        try {
            const response = await fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(submissionData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to ${isEditing ? 'update' : 'add'} ledger entry. Status: ${response.status}`);
            }

            handleCloseModal();
            fetchLedgerData(startDate || null, endDate || null);

        } catch (error) {
            console.error(`Error ${isEditing ? 'updating' : 'adding'} ledger entry:`, error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteEntryClick = (entryId) => {
        setEntryToDelete(entryId);
        setShowDeleteConfirm(true);
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setEntryToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!entryToDelete) return;

        setIsSubmitting(true);
        const token = localStorage.getItem("token");
        if (!token) {
            setIsSubmitting(false);
            setShowDeleteConfirm(false);
            setEntryToDelete(null);
            navigate("/login");
            return;
        }

        try {
            const response = await fetch(`${constant.REACT_APP_API_URL}/ledger/${entryToDelete}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete ledger entry.');
            }

            setShowDeleteConfirm(false);
            setEntryToDelete(null);
            fetchLedgerData(startDate || null, endDate || null);

        } catch (error) {
            console.error("Error deleting ledger entry:", error);
            setShowDeleteConfirm(false);
            setEntryToDelete(null);
        } finally {
            setIsSubmitting(false);
        }
    };


    const handleDownloadLedger = () => {
        if (!openingBalanceRow && (!ledgerData || ledgerData.length === 0)) {
            console.warn("No data available to download.");
            return;
        }

        const formattedData = [];

        if (openingBalanceRow) {
            formattedData.push({
                "Date": openingBalanceRow.date ? new Date(openingBalanceRow.date).toLocaleDateString('en-GB') : "-",
                "Voucher": "Opening Balance",
                "Inv No": "-",
                "Credit": openingBalanceRow.credit.toFixed(2),
                "Debit": openingBalanceRow.debit.toFixed(2),
                "Pay Mode": "-",
                "Remarks": "-",
                "Balance": openingBalanceRow.balance,
            });
        }

        if (ledgerData) {
            ledgerData.forEach((ledger) => {

                formattedData.push({
                    "Date": ledger.transactionDate
                        ? new Date(ledger.transactionDate).toLocaleDateString('en-GB')
                        : "N/A",
                    "Voucher": ledger.voucherType || "-",
                    "Inv No": ledger.invoiceId?.invoiceNumber || "-",
                    "Credit": ledger.credit.toFixed(2),
                    "Debit": ledger.debit.toFixed(2),
                    "Pay Mode": ledger.paymentMode || "-",
                    "Remarks": ledger.description || "-",
                    "Balance": ledger.balance,
                });
            });
        }

        const closingBalance = ledgerData && ledgerData.length > 0
            ? ledgerData[ledgerData.length - 1].balance
            : (openingBalanceRow ? openingBalanceRow.balance : "0.00");

        // Add Closing Balance / Total Row
        formattedData.push({
            "Date": "-",
            "Voucher": "Closing Balance / Total",
            "Inv No": "-",
            "Credit": totalCredit.toFixed(2),
            "Debit": totalDebit.toFixed(2),
            "Pay Mode": "-",
            "Remarks": "-",
            "Balance": closingBalance,
        });

        // Generate CSV
        const csv = Papa.unparse({
            fields: [
                "Date",
                "Voucher",
                "Inv No",
                "Credit",
                "Debit",
                "Pay Mode",
                "Remarks",
                "Balance",
            ],
            data: formattedData,
        });

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const filename = `${partyName || 'Party'}-ledger-${startDate || "all"}-to-${endDate || "all"}.csv`;
        FileSaver.saveAs(blob, filename);
    };

    // CSS classes remain the same
    const inputClass = "block w-full py-2 px-3 text-sm leading-5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed";
    const thClass = "px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider";
    const tdClass = "px-6 py-4 whitespace-nowrap text-sm text-gray-700";
    const errorBoxClass = "text-center py-10 px-4 text-red-500 bg-red-50 rounded-md border border-red-200";
    const linkClass = "text-blue-600 hover:text-blue-800 hover:underline";
    const smallDangerButtonClass = "inline-flex items-center px-3 py-2 bg-red-500 text-white text-sm font-medium rounded-md transition duration-150 ease-in-out hover:bg-red-600 disabled:opacity-50";
    const modalOverlayClass = "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60";
    const modalContentClass = "bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl";


    if (!isVisible) {
        return null;
    }

    return (
        <div>
            {/* Add/Edit Modal */}
            <AddLedger
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                initialData={editingEntry}
                partyId={partyId}
                isLoading={isSubmitting}
            />

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className={modalOverlayClass}>
                    <div className={modalContentClass}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Deletion</h3>
                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to delete this ledger entry? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={handleCancelDelete}
                                disabled={isSubmitting}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={isSubmitting}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                            >
                                {isSubmitting && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters and Actions Section */}
            <div className="mb-6 p-4 flex flex-wrap items-end gap-4 border-b border-gray-200">
                {/* Start Date */}
                <div className="flex-1 min-w-[150px]">
                    <label htmlFor="ledgerStartDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input type="date" id="ledgerStartDate" name="startDate" value={startDate} onChange={handleDateChange} className={inputClass} max={endDate || undefined} /> {/* Added max constraint */}
                </div>
                {/* End Date */}
                <div className="flex-1 min-w-[150px]">
                    <label htmlFor="ledgerEndDate" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input type="date" id="ledgerEndDate" name="endDate" value={endDate} onChange={handleDateChange} min={startDate || undefined} className={inputClass} /> {/* No disabled needed, min handles it */}
                </div>


                {/* Filtering Loader */}
                {isFilteringLedger && <div className="pt-5"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>}

                {/* Clear Filters Button */}
                {(startDate || endDate) && !isFilteringLedger && (
                    <div className="pt-5">
                        <button onClick={clearDateFilters} className={smallDangerButtonClass} title="Clear Date Filters">
                            <FilterX size={16} className="mr-1" /> Clear
                        </button>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center gap-2 pt-5">
                    {/* Add Entry Button */}
                    <button
                        onClick={handleAddEntryClick}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-150 disabled:opacity-50"
                        title="Add New Ledger Entry"
                        disabled={ledgerLoading || isSubmitting}
                    >
                        <PlusCircle size={18} className="mr-2" />
                        Add Entry
                    </button>

                    {/* Download Button */}
                    <button
                        onClick={handleDownloadLedger}
                        className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors duration-150 disabled:opacity-50"
                        disabled={ledgerLoading || isSubmitting || (!openingBalanceRow && (!ledgerData || ledgerData.length === 0))}
                        title="Download Ledger as CSV"
                    >
                        <Download size={18} className="mr-2" />
                        Download
                    </button>
                </div>
            </div>

            {ledgerLoading && !isFilteringLedger ? (
                <div className="min-h-[200px] flex items-center justify-center"><Loader2 className="h-10 w-10 animate-spin text-blue-600" /></div>
            ) : ledgerError ? (
                <div className={errorBoxClass}>{ledgerError}</div>
            ) : (openingBalanceRow || (ledgerData && ledgerData.length > 0)) ? (
                <>
                    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mb-4">
                        <div className="min-w-full overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className={thClass}>Date</th>
                                        <th className={thClass}>Voucher</th>
                                        <th className={thClass}>Inv No</th>
                                        <th className={`${thClass} text-right`}>Credit</th>
                                        <th className={`${thClass} text-right`}>Debit</th>
                                        <th className={thClass}>Pay Mode</th>
                                        <th className={thClass}>Remarks</th>
                                        <th className={`${thClass} text-right`}>Balance</th>
                                        <th className={`${thClass} text-center`}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">

                                    {openingBalanceRow && (
                                        <tr className="bg-gray-50 font-semibold">
                                            <td className={tdClass}>{openingBalanceRow.date ? new Date(openingBalanceRow.date).toLocaleDateString('en-GB') : "-"}</td>
                                            <td className={tdClass}>Opening Balance</td>
                                            <td className={tdClass}>-</td>
                                            <td className={`${tdClass} text-right text-green-700`}>{openingBalanceRow.credit.toFixed(2)}</td>
                                            <td className={`${tdClass} text-right text-red-700`}>{openingBalanceRow.debit.toFixed(2)}</td>
                                            <td className={tdClass}>-</td>
                                            <td className={tdClass}>-</td>
                                            <td className={`${tdClass} text-right`}>{openingBalanceRow.balance}</td>
                                            <td className={`${tdClass} text-center`}>
                                                {/* {(openingBalanceRow.credit > 0 || openingBalanceRow.debit) > 0 ? (
                                                      <div className="flex justify-start items-start space-x-2">
                                                            <button
                                                                onClick={() => handleEditEntryClick(openingBalanceRow.id)}
                                                                txt={openingBalanceRow.id}
                                                                className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                title="Edit Entry"
                                                                disabled={isSubmitting}
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                                                         
                                                        </div>
                                                ): (
                                                    <span className="text-gray-400">-</span>
                                                )} */}
                                                 <span className="text-gray-400">-</span>
                                            </td>
                                        </tr>
                                    )}

                                    {/* Ledger Data Rows - Rendered from ledgerData state */}
                                    {ledgerData && ledgerData.map((ledger) => {
                                        const canEditDelete = ledger.voucherType !== 'opening' && ledger.voucherType !== 'sales';

                                        return (
                                            <tr key={ledger._id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className={tdClass}>{ledger.transactionDate ? new Date(ledger.transactionDate).toLocaleDateString('en-GB') : "N/A"}</td>
                                                <td className={`${tdClass} capitalize`}>{ledger.voucherType || "-"}</td>
                                                <td className={tdClass}>
                                                    {/* Link Sale Invoices */}
                                                    {ledger.voucherType === 'sale' && ledger.invoiceId?._id ? (
                                                        <Link to={`/invoice/${ledger.invoiceId._id}`} target="_blank" rel="noopener noreferrer" className={linkClass} title="View Sale Invoice">
                                                            {ledger.invoiceId?.invoiceNumber || "-"}
                                                        </Link>
                                                    ) : (
                                                        ledger.invoiceId?.invoiceNumber || "-"
                                                    )}
                                                </td>
                                                <td className={`${tdClass} text-right text-green-700`}>{ledger.credit.toFixed(2)}</td>
                                                <td className={`${tdClass} text-right text-red-700`}>{ledger.debit.toFixed(2)}</td>
                                                <td className={`${tdClass} capitalize`}>{ledger.paymentMode || "-"}</td>
                                                <td className={`${tdClass} truncate max-w-[150px] md:max-w-xs`} title={ledger.description || ""}>{ledger.description || "-"}</td>
                                                <td className={`${tdClass} text-right`}>{ledger.balance}</td>
                                                {/* Actions Cell */}
                                                <td className={`${tdClass} text-center`}>
                                                    {canEditDelete ? (
                                                        <div className="flex justify-center items-center space-x-2">
                                                            <button
                                                                onClick={() => handleEditEntryClick(ledger)}
                                                                className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                title="Edit Entry"
                                                                disabled={isSubmitting}
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteEntryClick(ledger._id)}
                                                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                title="Delete Entry"
                                                                disabled={isSubmitting}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {(openingBalanceRow || (ledgerData && ledgerData.length > 0)) && (
                                        <tr className="bg-gray-100 font-semibold">
                                            <td className={tdClass}>-</td>
                                            <td colSpan="2" className={`${tdClass} font-semibold`}>Total / Closing Balance</td>
                                            <td className={`${tdClass} text-right text-green-800 font-semibold`}>{totalCredit.toFixed(2)}</td>
                                            <td className={`${tdClass} text-right text-red-800 font-semibold`}>{totalDebit.toFixed(2)}</td>
                                            <td colSpan="2" className={tdClass}>-</td>
                                            <td className={`${tdClass} text-right font-semibold`}>
                                                {ledgerData && ledgerData.length > 0
                                                    ? ledgerData[ledgerData.length - 1].balance
                                                    : (openingBalanceRow ? openingBalanceRow.balance : "0.00")
                                                }
                                            </td>
                                            <td className={`${tdClass} text-center`}>-</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : (

                !ledgerLoading && !ledgerError && (
                    <div className="text-center text-gray-500 py-10 px-4">
                        <p>No ledger entries found for this party{startDate && endDate ? " in the selected date range." : "."}</p>
                        <p className="mt-2 text-sm">You can add a new entry using the "Add Entry" button above.</p>
                    </div>
                )
            )}

        </div>
    );
};

export default Ledger;
